import React, {useEffect, useState} from "react";
import {DownOutlined} from "@ant-design/icons";
import {DatePicker, Input, Select, SelectProps, Space} from "antd";
import * as styles from "./TableFilter.module.css";
import dayjs from "dayjs";
import {FilterFunc} from "rc-select/lib/Select";

export interface SelectEditorProps {
    placeholder?: string;
    multiple: boolean;
    showSearch?: boolean;
    optionLabelProp?: string;
    optionFilterProp?: string;
    options?: SelectProps['options'];
    children?: React.ReactNode;
    filterOption?: boolean | FilterFunc<any>;
    value?: string | string[],
    onChange: (value: any) => any;
    width?: number;
    minWidth?: number;
    popupMatchSelectWidth?: boolean | number;
}

export function SelectEditor(props: SelectEditorProps) {
    const style: React.CSSProperties = {
        ...(props.width ? {width: props.width} : {}),
        ...(props.minWidth ? {minWidth: props.minWidth} : {})
    };

    return (
        <span>
            <Select
                size={"small"}
                className={styles["editor-select"]}
                style={Object.keys(style).length > 0 ? style : undefined}
                placeholder={props.placeholder}
                popupMatchSelectWidth={props.popupMatchSelectWidth ?? 350}
                mode={(props.multiple) ? "multiple" : undefined}
                showSearch={props.showSearch}
                suffixIcon={(props.multiple) ? null : <DownOutlined/>}
                value={props.value}
                onChange={props.onChange}
                options={props.options}
                optionLabelProp={props.optionLabelProp}
                optionFilterProp={props.optionFilterProp}
                filterOption={props.filterOption}
                allowClear={!props.multiple}
                virtual={false}
                listHeight={300}
            >
                {props.children}
            </Select>
        </span>
    );
}

export interface DatePickerEditorProps {
    placeholder?: string;
    value?: dayjs.Dayjs,
    presets?: {label: React.ReactNode, value: dayjs.Dayjs | (() => dayjs.Dayjs)}[],
    onChange: (value: any) => any;
}

export function DatePickerEditor(props: DatePickerEditorProps) {
    return (
        <DatePicker
            className={styles["editor-date-picker"]}
            size={"small"}
            allowClear={false}
            showTime={{defaultValue: dayjs('00:00:00', 'HH:mm:ss')}}
            placeholder={props.placeholder}
            presets={props.presets}
            value={props.value}
            onChange={props.onChange}
        />
    )
}

export interface InputEditorProps {
    placeholder?: string;
    status?: "error" | "warning";
    value?: string,
    onChange: (value: any) => any;
}

export function InputEditor(props: InputEditorProps) {
    const [text, setText] = useState("");

    useEffect(() => {
        setText(props.value);
    }, [props.value]);

    return (
        <Input
            className={styles["editor-input"]}
            size={"small"}
            placeholder={props.placeholder}
            status={props.status}
            value={text}
            onKeyUp={onKeyUp}
            onBlur={onBlur}
            onChange={e => setText(e.target.value)}
        />
    )

    function onKeyUp(event) {
        if (event.key === 'Enter') {
            props.onChange(text);
        }
    }

    function onBlur() {
        props.onChange(text);
    }

}

export interface AttributeEditorProps {
    options: SelectProps['options'];
    value?: { name: string, value: string },
    onChange: (value: any) => any;
}

export function AttributeEditor(props: AttributeEditorProps) {
    const spanRef = React.useRef<HTMLSpanElement>(null);
    const [inputWidth, setInputWidth] = useState(0);

    useEffect(() => {
        if (spanRef.current) {
            setInputWidth(spanRef.current.scrollWidth + 16);
        }
    }, [props.value]);

    return (
        <Space.Compact size={"small"} className={styles["editor-attribute"]}>
            <Select
                options={props.options}
                showSearch={true}
                value={props.value?.name}
                popupMatchSelectWidth={false}
                onChange={value => props.onChange(Object.assign({}, props.value, {name: value}))}
            />

            <Input
                style={{width: inputWidth}}
                value={props.value?.value}
                onChange={e => {
                    props.onChange(Object.assign({}, props.value, {value: e.target.value}))
                }}
            />
            <span ref={spanRef} className={styles["input-measure-node"]}>{props.value?.value}</span>
        </Space.Compact>
    )
}

export interface FilterItemProps {
    value?: any;
    draftValues?: any;
    onChange: (value?: any) => void;
    onDraftValuesChange?: (values: any) => void;
}

export interface FilterItem {
    name: string;
    label: string | React.ReactNode;
    labelCursor?: string;
    onLabelClick?: () => void,
    key: string;
    multiple?: boolean;
    initialValue?: any | (() => any);
    render: (props: FilterItemProps) => React.ReactNode;
}

export interface TableFilterProps {
    className?: string;
    items: FilterItem[];
    values?: any;
    defaultValues?: any;
    state?: SearchFilterState;
    persistentStateIdent?: string;
    defaultActiveFilterNames?: string[];
    immediateMode?: boolean,
    onChange: (values: any) => void;
    onStateChange?: (state: SearchFilterState) => void;
}

export interface SearchFilterState {
    activeFilterNames?: string[];
    inactiveDefaultFilterNames?: string[];
    activeItemOrder?: string[];
}
